import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import LazyImage from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React from "react";
import { connect } from "react-redux";

export const enum fields {
    image = "image",
    name = "name",
    title = "title",
    buttonLabel = "buttonLabel",
    buttonLink = "buttonLink",
    buttonVariant = "buttonVariant",

    displayImage = "Image",
    displayName = "Title",
    displayTitle = "Description",
    displayButtonLabel = "Button Label",
    displayButtonLink = "Button Link",
    displayButtonVariant = "Button Variant",
}

export interface OwnProps extends WidgetProps {
    fields: {
        [fields.image]: string;
        [fields.name]: string;
        [fields.title]: string;
        [fields.buttonLabel]: string;
        [fields.buttonLink]: LinkFieldValue;
        [fields.buttonVariant]: "primary" | "secondary" | "tertiary";
    };
}

export const mapStateToProps = (_: ApplicationState, __: OwnProps) => {
    return {};
};

export type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface CategoryCardWidgetStyles {
    wrapper?: InjectableCss;
    thumbnail?: InjectableCss;
    textWrapper?: InjectableCss;
    name?: TypographyPresentationProps;
    title?: TypographyPresentationProps;
    link?: LinkPresentationProps;
    linkCard?: LinkPresentationProps;
}

export const categoryCardWidgetStyles: CategoryCardWidgetStyles = {};

export const styles = categoryCardWidgetStyles;

export const CategoryCardWidget = ({ fields: { name, title, image, buttonLabel, buttonLink } }: Props) => {
    const { isInShell } = useShellContext();
    const history = useHistory();

    const buttonLinkValue = useGetLinks([buttonLink], o => o);

    const onClick = (url: string | undefined) => {
        if (!url) {
            return;
        }

        if (url.startsWith("http")) {
            if (isInShell) {
                return;
            }

            window.location.href = url;
        } else {
            history.push(url);
        }
    };

    const isExternalLink = buttonLink.value?.startsWith("http");
    const linkProps = isExternalLink ? { target: "_blank", rel: "noopener noreferrer" } : {};

    const cardContent = (
        <StyledWrapper {...styles.wrapper}>
            <StyledWrapper {...styles.thumbnail}>
                <LazyImage src={image} altText={`${name}, ${title}`} />
            </StyledWrapper>
            <StyledWrapper {...styles.textWrapper}>
                <Typography {...styles.name}>{name}</Typography>
                <Typography {...styles.title}>{title}</Typography>
                {buttonLabel && (
                    <Link {...styles.link} {...linkProps}>
                        {buttonLabel}
                    </Link>
                )}
            </StyledWrapper>
        </StyledWrapper>
    );

    if (buttonLinkValue) {
        return (
            <Link href={buttonLinkValue[0].url} {...linkProps} {...styles.linkCard}>
                {cardContent}
            </Link>
        );
    }

    return cardContent;
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(CategoryCardWidget) as any,
    definition: {
        displayName: "Category Card",
        group: "Verndale Box" as any,
        icon: "user-tie",
        fieldDefinitions: [
            {
                name: fields.image,
                displayName: fields.displayImage,
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "Translatable",
                isRequired: true,
            },
            {
                name: "recommendedSize",
                displayName: "Use recommended size (360x200)",
                defaultValue: "",
                editorTemplate: "CustomLabelField" as any,
                fieldType: "Translatable",
            },
            {
                name: fields.name,
                displayName: fields.displayName,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                isRequired: true,
            },
            {
                name: fields.title,
                displayName: fields.displayTitle,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                isRequired: true,
            },
            {
                name: fields.buttonLabel,
                displayName: fields.displayButtonLabel,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
            },
            {
                name: "buttonLink",
                displayName: "Button Link",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
                fieldType: "Translatable",
            },
        ],
    },
};

export default widgetModule;
